import {Nullable} from '../../../app/TypeHelpers'
import {ASSOC_CHAIRMAN_REGISTRATION_STATUS, RESIDENT_STATUS, ROLE_FOR_ADMINISTRATOR,} from '../../../CodeMaster'
import {Moment} from 'moment/moment'
import moment from 'moment'
import { SortDirection } from '@mui/material';

export interface AssocStatusCondominiumInfo {
    condominiumCd: string
}

export function isAssocStatusCondominiumInfo(value: unknown): value is AssocStatusCondominiumInfo {
    if (value === null || typeof value !== 'object') {
        return false
    }

    const maybeAssocStatusCondominiumInfo = value as Record<keyof AssocStatusCondominiumInfo, unknown>

    return typeof maybeAssocStatusCondominiumInfo.condominiumCd === 'string'
}

export function isAssocStatusCondominiumInfoList(valueList: unknown): valueList is AssocStatusCondominiumInfo[] {
    if (!Array.isArray(valueList)) {
        return false
    }
    return valueList.every(value => isAssocStatusCondominiumInfo(value))
}

export interface AssocStatusSearchCondition {
    condominiumGroupId: number
    condominiumCdList: string[]
    keyword: Nullable<string>
    assocChairmanRegistrationStatusList: string[]
    sortKey: string
    sortDirection: SortDirection
    rowsPerPage: number
    page: number
}

export interface AssocStatusResponse {
    condominiumCd: string
    condominiumGroupName: string
    condominiumName: string
    assocId: number
    assocName: string
    assocChairmanStatus: AssocChairmanStatusResponse
    assocStatusMemo: Nullable<AssocStatusMemoResponse>
}

interface AssocChairmanStatusResponse {
    assocStatus: string
    currentTermStatus: Nullable<AssocTermStatusResponse>
    nextTermStatus: Nullable<AssocTermStatusResponse>
}

interface AssocTermStatusResponse {
    termName: string
    termStartDate: string
    termEndDate: string
    assocChairmanList: AssocChairmanResponse[]
    hasChairmanAbsentTerm: boolean
}

interface AssocChairmanResponse {
    assocChairmanRoomInfoList: AssocChairmanRoomInfoResponse[]
    assocChairmanName: Nullable<string>
}

interface AssocChairmanRoomInfoResponse {
    ridgeName: string
    roomNumber: string
    residentStatus: string
}

interface AssocStatusMemoResponse {
    content: string
    updateUserFamilyName: string
    updateUserFirstName: string
    updateUserRoleId: string
    updateDateTime: string
    version: number
}

interface AssocChairmanStatus {
    assocStatus: ASSOC_CHAIRMAN_REGISTRATION_STATUS
    currentTermStatus: Nullable<AssocTermStatus>
    nextTermStatus: Nullable<AssocTermStatus>
}

export interface AssocTermStatus {
    termName: string
    termStartDate: Moment
    termEndDate: Moment
    assocChairmanList: AssocChairman[]
    hasChairmanAbsentTerm: boolean
}

interface AssocChairman {
    assocChairmanRoomInfoList: AssocChairmanRoomInfo[]
    assocChairmanName: string
}

interface AssocChairmanRoomInfo {
    ridgeName: string
    roomNumber: string
    residentStatus: RESIDENT_STATUS
}

interface AssocStatusMemo {
    content: string
    updateUserFamilyName: string
    updateUserFirstName: string
    updateUserRole: ROLE_FOR_ADMINISTRATOR
    updateDateTime: Moment
    version: number
}

export class AssocStatus {
    condominiumCd: string
    condominiumGroupName: string
    condominiumName: string
    assocId: number
    assocName: string
    assocChairmanStatus: AssocChairmanStatus
    assocStatusMemo: Nullable<AssocStatusMemo>

    constructor(
        condominiumCd: string,
        condominiumGroupName: string,
        condominiumName: string,
        assocId: number,
        assocName: string,
        assocChairmanStatus: AssocChairmanStatus,
        assocStatusMemo: Nullable<AssocStatusMemo>
    ) {
        this.condominiumCd = condominiumCd
        this.condominiumGroupName = condominiumGroupName
        this.condominiumName = condominiumName
        this.assocId = assocId
        this.assocName = assocName
        this.assocChairmanStatus = assocChairmanStatus
        this.assocStatusMemo = assocStatusMemo
    }

    static from(assocStatusResponse: AssocStatusResponse): AssocStatus {
        const assocChairmanStatus = assocStatusResponse.assocChairmanStatus

        return new AssocStatus(
            assocStatusResponse.condominiumCd,
            assocStatusResponse.condominiumGroupName,
            assocStatusResponse.condominiumName,
            assocStatusResponse.assocId,
            assocStatusResponse.assocName,
            {
                assocStatus: ASSOC_CHAIRMAN_REGISTRATION_STATUS.codeToEnumConstants(
                    assocChairmanStatus.assocStatus
                ),
                currentTermStatus: assocChairmanStatus.currentTermStatus
                    ? {
                        termName: assocChairmanStatus.currentTermStatus.termName,
                        termStartDate: moment(assocChairmanStatus.currentTermStatus.termStartDate),
                        termEndDate: moment(assocChairmanStatus.currentTermStatus.termEndDate),
                        assocChairmanList: assocChairmanStatus.currentTermStatus.assocChairmanList
                            .map((assocChairman: any) => ({
                                assocChairmanRoomInfoList: assocChairman.assocChairmanRoomInfoList
                                    .map((roomInfo: any) => ({
                                        ridgeName: roomInfo.ridgeName,
                                        roomNumber: roomInfo.roomNumber,
                                        residentStatus: RESIDENT_STATUS.codeToEnumConstants(roomInfo.residentStatus),
                                    })),
                                assocChairmanName: assocChairman.assocChairmanName,
                            })),
                        hasChairmanAbsentTerm: assocChairmanStatus.currentTermStatus.hasChairmanAbsentTerm
                    }
                    : null,
                nextTermStatus: assocChairmanStatus.nextTermStatus
                    ? {
                        termName: assocChairmanStatus.nextTermStatus.termName,
                        termStartDate: moment(assocChairmanStatus.nextTermStatus.termStartDate),
                        termEndDate: moment(assocChairmanStatus.nextTermStatus.termEndDate),
                        assocChairmanList: assocChairmanStatus.nextTermStatus.assocChairmanList
                            .map((assocChairman: any) => ({
                                assocChairmanRoomInfoList: assocChairman.assocChairmanRoomInfoList
                                    .map((roomInfo: any) => ({
                                        ridgeName: roomInfo.ridgeName,
                                        roomNumber: roomInfo.roomNumber,
                                        residentStatus: RESIDENT_STATUS.codeToEnumConstants(roomInfo.residentStatus),
                                    })),
                                assocChairmanName: assocChairman.assocChairmanName,
                            })),
                        hasChairmanAbsentTerm: assocChairmanStatus.nextTermStatus.hasChairmanAbsentTerm
                    }
                    : null,
            },
            assocStatusResponse.assocStatusMemo
                ? {
                    content: assocStatusResponse.assocStatusMemo.content,
                    updateUserFamilyName: assocStatusResponse.assocStatusMemo.updateUserFamilyName,
                    updateUserFirstName: assocStatusResponse.assocStatusMemo.updateUserFirstName,
                    updateUserRole: ROLE_FOR_ADMINISTRATOR.codeToEnumConstants(
                        assocStatusResponse.assocStatusMemo.updateUserRoleId
                    ),
                    updateDateTime: moment(assocStatusResponse.assocStatusMemo.updateDateTime),
                    version: assocStatusResponse.assocStatusMemo.version
                }
                : null
        )
    }
}
